import { useEffect, useState } from 'react';
import { ArrowLeft, Loader2, Printer } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';

import { businessFulfillmentService } from '../../services/businessFulfillmentService';
import { buildParcelLabel } from '../../../features/delivery/parcelLabel';
import { PageHeader } from '../../../components/ui';

/**
 * The printed Parcel label.
 *
 * It used to carry only the identity code and its QR, which meant a parcel that
 * got separated from the Rider's phone was an anonymous bag - the app knew where
 * it was going and the parcel itself did not. It now carries its own
 * destination, the way a courier label does, so it stays deliverable when the
 * phone is dead, the app is down, or it has been put in the wrong pile.
 *
 * Laid out for a 4 x 6 in thermal label, which is the standard stock, with the
 * destination given the most ink because that is what a person reads from an
 * arm's length away.
 */
export default function ParcelLabelPrintPage() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const query = params.toString();
  const labelPath = `/business/orders/fulfillment/${orderId}/label${query ? `?${query}` : ''}`;

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    businessFulfillmentService.detail(orderId, controller.signal)
      .then((nextOrder) => {
        if (!cancelled) setOrder(nextOrder);
      })
      .catch(() => {
        if (!cancelled && !controller.signal.aborted) {
          setError('The Parcel label could not be loaded.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [orderId]);

  if (loading) return <div role="status" className="grid min-h-64 place-items-center"><Loader2 className="h-7 w-7 animate-spin" aria-hidden="true" /><span className="sr-only">Loading Parcel label…</span></div>;
  if (order && !order.parcelLabel) return <Navigate replace to={labelPath} />;
  if (error || !order) return <div role="alert" className="rounded-xl border border-danger bg-danger-subtle p-4 text-danger">{error || 'Parcel label not found.'}</div>;

  const label = buildParcelLabel(order);
  if (!label.printable) {
    // A label with no destination looks finished and cannot be delivered, which
    // is worse than not printing one.
    return <div role="alert" className="rounded-xl border border-warning bg-warning-subtle p-4 text-warning">
      <p className="font-semibold">This label cannot be printed yet</p>
      <p className="mt-1 text-sm leading-6">{label.reason}</p>
      <Link to={labelPath} className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-xl border border-warning px-4 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"><ArrowLeft className="h-4 w-4" aria-hidden="true" />Back to label step</Link>
    </div>;
  }

  return <main className="parcel-label-print-page space-y-5 text-neutral-950 dark:text-white">
    <div className="print-controls flex flex-wrap items-center justify-between gap-3">
      <Link to={labelPath} className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-neutral-300 px-4 text-sm font-semibold dark:border-neutral-700"><ArrowLeft className="h-4 w-4" aria-hidden="true" />Back to label step</Link>
      <button type="button" onClick={() => window.print()} className="business-primary-action inline-flex min-h-11 items-center gap-2 rounded-xl bg-accent px-4 text-sm font-semibold text-on-accent hover:bg-accent-hover active:bg-accent-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"><Printer className="h-4 w-4" aria-hidden="true" />Print or save PDF</button>
    </div>

    <PageHeader className="print-controls" title="Parcel label" description="Print preview. Primary output is 4 × 6 inches. Stick it where the whole label stays flat and readable." />

    <article className="parcel-label-sheet mx-auto flex aspect-[2/3] w-full max-w-[4in] flex-col border-2 border-black bg-white p-[0.18in] text-black">
      {/* Store and sort token: what a rider reads first, from an arm away. */}
      <div className="flex items-start justify-between gap-2 border-b-2 border-black pb-2">
        <div className="min-w-0">
          <p className="text-[9px] font-bold uppercase tracking-[0.18em]">Apparify Parcel</p>
          <p className="truncate text-sm font-bold">{label.returnTo.businessName || 'Apparify seller'}</p>
        </div>
        {/* SORT: the same short tag the rider's app shows beside this stop, so
            the right bag can be found at a doorstep without reading the code. */}
        {label.sortToken && <div className="shrink-0 text-center">
          <p className="text-[8px] font-bold uppercase tracking-[0.18em]">Sort</p>
          <p className="border-2 border-black px-2 py-0.5 text-2xl font-black leading-tight tracking-wider">{label.sortToken}</p>
        </div>}
      </div>

      {/* Deliver to: the reason the label exists. Biggest block on the sheet. */}
      <div className="border-b-2 border-black py-2">
        <p className="text-[9px] font-bold uppercase tracking-[0.18em]">Deliver to</p>
        <p className="mt-0.5 text-lg font-black leading-tight">{label.shipTo.name}</p>
        {label.shipTo.lines.map((line) => (
          <p key={line} className="text-[11px] font-semibold leading-snug">{line}</p>
        ))}
        {label.shipTo.phone && <p className="mt-1 text-[11px] font-bold">{label.shipTo.phone}</p>}
      </div>

      <div className="flex flex-1 items-center justify-between gap-2 py-2">
        <div className="min-w-0">
          <p className="text-[9px] font-bold uppercase tracking-[0.18em]">Order</p>
          <p className="text-sm font-bold">{label.orderReference}</p>
          <p className="mt-2 text-[9px] font-bold uppercase tracking-[0.18em]">Parcel</p>
          {/* Printed in full so it can be read out or typed if the QR will not
              scan - a damaged code must not strand the parcel. */}
          <p className="break-all font-mono text-[10px] font-bold leading-snug">{label.code}</p>
        </div>
        <div className="shrink-0 text-center">
          <p className="text-[8px] font-bold uppercase tracking-[0.18em]">Scan</p>
          {/* size is the whole stamp, code plus quiet zone - 174 renders a 140px
              code inside the 4-module white border the QR spec requires. Without
              marginSize the code runs to the edge of its own box, and on a dark
              bag the scanner has nothing light to find the finder patterns
              against, which is what made these hard to scan. */}
          <QRCodeSVG value={label.qrPayload} size={174} marginSize={4} level="M" title="Parcel identity QR" />
        </div>
      </div>

      <div className="border-t-2 border-black pt-1.5 text-[9px] leading-snug">
        <p className="font-bold uppercase tracking-[0.14em]">If undelivered, return to</p>
        <p className="font-semibold">{label.returnTo.businessName}{label.returnTo.address ? ` — ${label.returnTo.address}` : ''}</p>
      </div>
    </article>
  </main>;
}
